import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import withSize from 'react-sizeme';
import * as D3Shape from 'd3-shape';
import * as D3Scale from 'd3-scale';
import * as D3Array from 'd3-array';
import * as D3Drag from 'd3-drag';
import * as D3Selection from 'd3-selection';

import { isNumeric, numberFormat } from '~/core/scripts/utils';
import { is360Query, is768Query } from '~/core/scripts/mediaquery-helper';
import { clamp, translate } from '~/core/scripts/chart-components/utils';
import ChartWrapper from '~/core/scripts/chart-components/ChartWrapper';
import XAxis from '~/core/scripts/chart-components/XAxis';
import YAxis from '~/core/scripts/chart-components/YAxis';
import Grid from '~/core/scripts/chart-components/Grid';
import { getGapData, fillGap, yGetter, setUserPoints, sendData, hasUserStartedToDraw, calcError } from './utils';
import ResultCard from './ResultCard';
import CallToAction from './CallToAction';

import './YouDrawIt.styl';

const d3 = {
  ...D3Shape,
  ...D3Scale,
  ...D3Array,
  ...D3Drag,
  ...D3Selection
};

let ydiChartId = 0;
const getId = () => ydiChartId++;

class YouDrawIt extends PureComponent {
  static propTypes = {
    chartId: PropTypes.string.isRequired,
    data: PropTypes.arrayOf(PropTypes.shape({
      x: PropTypes.number,
      y: PropTypes.number
    })).isRequired,
    xAxis: PropTypes.array.isRequired,
    yAxis: PropTypes.array.isRequired,
    yDecimals: PropTypes.number,
    shareTitle: PropTypes.string.isRequired,
    yAxisUnit: PropTypes.string,
    showYAxis: PropTypes.bool,
    dataVisibleIndex: PropTypes.number,
    serverUrl: PropTypes.string,
    linecolor: PropTypes.string,
    usercolor: PropTypes.string,
    gapcolor: PropTypes.string,
    servercolor: PropTypes.string,
    showCallToAction: PropTypes.bool,
    showLockButton: PropTypes.bool,
    callToActionLabel: PropTypes.string,
    margin: PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number
    }),
    ratio: PropTypes.shape({
      desktop: PropTypes.number,
      mobile: PropTypes.number
    }),
    onCompleted: PropTypes.func
  }

  static defaultProps = {
    dataVisibleIndex: 0,
    yAxisUnit: null,
    showYAxis: true,
    serverUrl: 'https://drawit-api.herokuapp.com/',
    linecolor: '#8D76E0',
    usercolor: '#FFA600',
    gapcolor: '#FFFCB4',
    servercolor: '#A3A3A3',
    showCallToAction: true,
    callToActionLabel: 'Zeichnen Sie die Linie weiter',
    yDecimals: 1,
    margin: { top: 0, right: 0, bottom: 0, left: 0 },
    ratio: { desktop: .6, mobile: 1 },
    onCompleted: () => {},
    showLockButton: true
  }

  constructor(props) {
    super();

    this.chartId = getId();
    this.chartwrapper = React.createRef();

    this.data = props.data.map(d => ({
      ...d,
      defined: true
    }));

    this.xAxis = typeof props.xAxis[0] === 'number' ?
      props.xAxis.map(value => ({ value, label: value })) :
      props.xAxis;

    this.yAxis = typeof props.yAxis[0] === 'number' ?
      props.yAxis.map(value => ({ value, label: value })) :
      props.yAxis;

    this.margin = this.getMargin(props);

    // we need to know the space between the steps
    // for handling the drag updates
    this.stepDiff = this.data[1].x - this.data[0].x;
    this.gridValues = {
      y: this.yAxis.slice(1, this.yAxis.length),
      x: this.data.map(d => ({ value: d.x }))
    };

    this.state = {
      userData: this.getInitialUserData(props),
      completed: false,
      isLoading: false,
      serverResponse: null,
      locked: !is768Query() && props.showCallToAction && props.showLockButton
    };

  }

  getMargin(props) {
    const isMobile = !is768Query();

    const minTop = isMobile ? 20 : 10;
    const minRight = isMobile ? 5 : 30;
    const minBottom = 20;
    const minLeft = props.showYAxis ? 25 : 5;
    
    props.margin.right = isMobile ? 0 : props.margin.right;

    return {
      top: minTop + props.margin.top,
      right: minRight + props.margin.right,
      bottom: minBottom + props.margin.bottom,
      left: minLeft + props.margin.left
    };
  }

  getHeight = (width) => {
    // if (is768Query()) {
    //   return 500;
    // }
    return 400;
  }

  getInitialUserData(props) {
    const lastVisibleXValue = this.data[props.dataVisibleIndex].x;

    return this.data
      .map(d => ({ ...d, defined: d.x === lastVisibleXValue }))
      .filter(d => d.x >= lastVisibleXValue);
  }

  componentDidMount() {
    this.initDrag(this.state.locked);
  }

  initDrag = (isLocked) => {
    if (this.isInitialized || !this.chartwrapper.current || isLocked) {
      return false;
    }

    const dragger = d3.drag()
      .on('drag', this.onDrag)
      .on('end', this.onDragEnd);

    d3.select(this.chartwrapper.current.svg).call(dragger);
    this.isInitialized = true;
  }

  removeDrag = () => {
    if (!this.chartwrapper.current) {
      return false;
    }

    d3.select(this.chartwrapper.current.svg)
      .on('.drag', null)
      .style('touch-action', 'auto');

    this.isInitialized = false;
  }

  onDrag = (d, i, nodes) => {
    if (this.state.completed || this.state.locked) {
      return false;
    }

    const pos = d3.mouse(nodes[i]);
    const xVal = clamp(this.data[this.props.dataVisibleIndex + 1].x, this.data[this.data.length - 1].x, this.x.invert(pos[0]));
    const yVal = clamp(this.y.domain()[0], this.y.domain()[1], this.y.invert(pos[1]));
    const userData = this.state.userData.map(setUserPoints(xVal, yVal, this.stepDiff));
    const gapData = this.state.userData.reduce(getGapData, false);

    if (!gapData) {
      return this.setState({ userData });
    }

    const getY = yGetter(this.state.userData[gapData.minIndex], this.state.userData[gapData.maxIndex]);
    const userDataFilled = userData.map(fillGap(gapData.minIndex, gapData.maxIndex, getY));

    this.setState({ userData: userDataFilled });
  }

  onDragEnd = async () => {
    if (this.state.completed || d3.mean(this.state.userData, d => d.defined) !== 1) {
      return false;
    }

    const { yAxis } = this;

    const minYValue = yAxis[0].value;
    const maxYValue = yAxis[yAxis.length - 1].value;

    const error = calcError(this.data, this.state.userData, minYValue, maxYValue);
    this.setState({ completed: true, isLoading: true });
    this.removeDrag();
    this.props.onCompleted();

    const serverResponse = await sendData({
      url: this.props.serverUrl,
      chartId: this.props.chartId,
      error: error,
      userData: this.state.userData
    });

    this.setState({
      isLoading: false,
      serverResponse: {
        error,
        ...serverResponse
      }
    });
  }

  updateDimensions() {
    const width = ~~this.props.size.width;

    if (this.width === width) {
      return false;
    }

    const { margin } = this;
    const height = this.getHeight(width);

    this.x = d3.scaleLinear()
      .domain(d3.extent(this.data, d => d.x))
      .range([margin.left, width - margin.right]);

    this.y = d3.scaleLinear()
      .domain(d3.extent(this.yAxis, d => d.value))
      .range([height - margin.bottom, margin.top])
      .clamp(true);

    this.line = d3.line()
      .defined(d => d.defined)
      .x(d => this.x(d.x))
      .y(d => this.y(d.y));

    this.width = width;
    this.height = height;
  }

  drawServerLines() {
    if (!this.state.serverResponse) {
      return null;
    }

    return this.state.serverResponse.sample.map(s => {
      const points = s.points
        // we only want to draw valid server lines
        .filter(p => p[0] <= this.data[this.data.length - 1].x && p[0] >= this.data[this.props.dataVisibleIndex].x)
        .map(p => ({ x: p[0], y: p[1], defined: true }));

      return (
        <path
          key={s._id}
          d={this.line(points)}
          className="chart__line chart__serverline"
          stroke={this.props.servercolor}
        />
      );
    });
  }

  drawGapRect(height) {
    const definedData = this.state.userData.filter(d => d.defined);
    const rectData = [definedData[definedData.length - 1], ...this.state.userData.filter(d => !d.defined)];

    if (!rectData.length || rectData.length === 1) {
     return false;
    }

    const pointA = rectData[0];
    const pointB = rectData[rectData.length - 1];

    return (
      <rect
        x={this.x(pointA.x)}
        y={this.margin.top}
        width={this.x(pointB.x) - this.x(pointA.x)}
        height={height - this.margin.bottom}
        className="chart__gaprect"
        fill={this.props.gapcolor}
      />
    );
  }

  drawLabels = () => {
    const { userData, completed } = this.state;

    const isSmallScreen = !is768Query();
    const textAnchor = isSmallScreen ? 'end' : 'start';
    const definedData = userData.filter(d => d.defined);
    const lastDefined = definedData[definedData.length - 1];
    const lastData = this.data[this.data.length - 1];

    const x = this.x(lastData.x);
    const y0 = this.y(lastData.y);
    const y1 = this.y(lastDefined.y);
    const diff = y0 - y1;

    let dy0 = isSmallScreen ? -8 : 4;
    let dx0 = isSmallScreen ? 5 : 8;
    let dy1 = isSmallScreen ? -8 : 4;
    let dx1 = isSmallScreen ? 5 : 8;

    if (Math.abs(diff) < 30 && isSmallScreen && completed) {
      dy0 = diff > 0 ? 18 : dy0;
      dy1 = diff > 0 ? dy1 : 18;
    }

    if (Math.abs(diff) < 12 && !isSmallScreen && completed) {
      dy0 = diff > 0 ? dy0 + (12 - diff) : dy0 - (12 + diff);
    }

    return (
      <Fragment>
        <g
          className="chart__value"
          fill={this.props.linecolor}
          transform={`translate(${x}, ${y0})`}
          opacity={completed ? 1 : 0}
        >
          <text
            dx={dx0}
            dy={dy0}
            textAnchor={textAnchor}
          >
            {numberFormat(lastData.y, this.props.yDecimals, this.props.yAxisUnit)}
          </text>
          <circle r={5} />
        </g>

        <g
          className="chart__uservalue chart__value"
          fill={this.props.usercolor}
          transform={`translate(${x}, ${y1})`}
        >
          <text
            dx={dx1}
            dy={dy1}
            textAnchor={textAnchor}
          >
            {numberFormat(lastDefined.y, this.props.yDecimals, this.props.yAxisUnit)}
          </text>
          <circle r={5} />
        </g>
      </Fragment>
    );
  }

  drawValue = (d, i) => {
    if (i >= 2) {
      return null;
    }

    return (
      <g
        className="chart__value"
        fill={this.props.linecolor}
        key={`chart__value_${d.x}`}
        transform={translate(this.x(d.x), this.y(d.y))}
      >
        <text
          dx={0}
          textAnchor={i === 0 ? 'start' : 'middle'}
          dy={-8}
        >
          {numberFormat(d.y, this.props.yDecimals, this.props.yAxisUnit)}
        </text>
        <circle r={5} />
      </g>
    );
  }

  getHighlightValues() {
    return [
      this.data[0],
      this.data[this.props.dataVisibleIndex],
      this.data[this.data.length - 1]
    ];
  }

  onReset = () => {
    this.setState({
      completed: false,
      userData: this.getInitialUserData(this.props),
      serverResponse: null
    });

    this.initDrag();
  }

  onUnlock = () => {
    this.setState({ locked: false });
    this.initDrag();
  }

  render() {
    if (!isNumeric(this.props.size.width)) {
      return null;
    }

    this.updateDimensions();

    const { margin, props, height, width } = this;
    const { dataVisibleIndex, showYAxis, callToActionLabel, showCallToAction } = props;
    const lastVisibleXIndex = this.state.completed ? this.data.length - 1 : dataVisibleIndex
    const clipWidth = this.x(this.data[lastVisibleXIndex].x);
    const wrapperClasses = classnames({ 'is-completed': this.state.completed, 'is-locked': this.state.locked });
    const startedToDraw = hasUserStartedToDraw(this.state.userData);
    const highlightValues = this.getHighlightValues();
    const ctaWidth = width - clipWidth - margin.right;
    const ctaHeight = height - margin.top - margin.bottom;
    const ctaVisible = !startedToDraw && showCallToAction;

    return (
      <div className="youdrawit">
        <ChartWrapper
          width={width}
          height={height}
          ref={this.chartwrapper}
          className={wrapperClasses}
        >
          <clipPath className="ydi-clip" id={`ydi-clip__${this.chartId}`}>
            <rect width={clipWidth} height={height} />
          </clipPath>
          <Grid
            values={this.gridValues}
            margin={margin}
            y={this.y}
            x={this.x}
            height={height - margin.bottom - margin.top}
            width={width - margin.left - margin.right}
          />
          <g clipPath={`url(#ydi-clip__${this.chartId})`}>
            {this.drawServerLines()}
            <path
              className="chart__line"
              d={this.line(this.data)}
              stroke={this.props.linecolor}
            />
          </g>
          <path
            className="chart__line chart__userline"
            d={this.line(this.state.userData)}
            stroke={this.props.usercolor}
          />
          {highlightValues.map((d, i) => this.drawValue(d, i))}
          {startedToDraw && this.drawGapRect(height - margin.top)}
          {startedToDraw && this.drawLabels()}
          <XAxis
            ticks={this.xAxis}
            width={width}
            height={height}
            margin={margin}
            drawTickLines
            x={this.x}
          />
          {showYAxis && <YAxis
            ticks={this.yAxis}
            margin={margin}
            y={this.y}
            drawTickLines
            unit={this.props.yAxisUnit}
          />}
        </ChartWrapper>
        <ResultCard
          onRedraw={this.onReset}
          data={this.state.serverResponse}
          shareTitle={this.props.shareTitle}
        />
        <CallToAction
          isVisible={ctaVisible}
          width={ctaWidth}
          height={ctaHeight}
          margin={margin}
          label={callToActionLabel}
          isLocked={this.state.locked}
          onUnlock={this.onUnlock}
        />
      </div>
    );
  }
}

export default withSize({ monitorWidth: true, monitorHeight: false })(YouDrawIt);
